import { assertNever } from "@redotech/util/type";
import type { AnalyticsExpression } from "./analytics-query-expression-types";
import { analyticsFunctionSignature } from "./analytics-query-function-registry";
import type {
  AnalyticsFunctionArgRule,
  AnalyticsFunctionName,
} from "./analytics-query-function-types";
import type { AnalyticsNameSlot } from "./analytics-query-types";

/** Why argument `index` of `fn` breaks its constant parameter, or `undefined` when it fits or the parameter is not constant. */
export function analyticsConstantArgumentProblem(
  fn: AnalyticsFunctionName,
  index: number,
  arg: AnalyticsExpression<AnalyticsNameSlot>,
): string | undefined {
  const params = analyticsFunctionSignature(fn).params;
  const param = params[Math.min(index, params.length - 1)];
  if (param === undefined || fitsConstant(param.rule, arg)) {
    return undefined;
  }
  const expected = expectedConstant(param.rule);
  return expected === undefined
    ? undefined
    : `the ${param.name} argument of ${fn} must be ${expected}`;
}

function fitsConstant(
  rule: AnalyticsFunctionArgRule,
  arg: AnalyticsExpression<AnalyticsNameSlot>,
): boolean {
  switch (rule.kind) {
    case "constant_integer": {
      if (arg.kind !== "constant" || arg.value.type !== "quantity") {
        return false;
      }
      const value = arg.value.value;
      return (
        Number.isInteger(value) &&
        (rule.min === undefined || value >= rule.min) &&
        (rule.max === undefined || value <= rule.max)
      );
    }
    case "constant_text":
      return arg.kind === "constant" && arg.value.type === "text";
    case "any":
    case "typed":
    case "uniform":
      return true;
    default:
      return assertNever(rule);
  }
}

function expectedConstant(rule: AnalyticsFunctionArgRule): string | undefined {
  switch (rule.kind) {
    case "constant_integer":
      return `a whole-number quantity constant${integerBounds(rule.min, rule.max)}`;
    case "constant_text":
      return "a text constant";
    case "any":
    case "typed":
    case "uniform":
      return undefined;
    default:
      return assertNever(rule);
  }
}

function integerBounds(
  min: number | undefined,
  max: number | undefined,
): string {
  if (min !== undefined && max !== undefined) {
    return ` from ${min} to ${max}`;
  }
  if (min !== undefined) {
    return ` of at least ${min}`;
  }
  return max === undefined ? "" : ` of at most ${max}`;
}
